import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, LogOut, Camera, Save, X, ArrowLeft, ChevronRight, Bell, Smartphone, Info, Mail, Lock, Eye, EyeOff } from "lucide-react";
import BottomNav from "@/components/BottomNav";
import BottomNavAgent from "@/components/BottomNavAgent";
import { NotificationDiagnostics } from "@/components/NotificationDiagnostics";
import { PWAInstallGuide } from "@/components/PWAInstallPrompt";
import { AppVersionCard } from "@/components/AppVersionCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const UAE_EMIRATES = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
];

export default function ProfilePage() {
  const { user, profile, updateProfile, signOut, loading: authLoading, isAgent, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "+971",
    address_line1: "",
    address_line2: "",
    city: "Dubai",
    emirate: "Dubai",
    postal_code: "",
    marketing_opt_in: false,
  });

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 10 Mo");
      return;
    }

    setUploadingAvatar(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/avatar.${fileExt}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update profile with avatar URL (add cache buster)
      const avatarUrl = `${publicUrl}?t=${Date.now()}`;
      const { error: updateError } = await updateProfile({ avatar_url: avatarUrl });

      if (updateError) throw updateError;

      await refreshProfile();
      toast.success("Photo de profil mise à jour");
    } catch (error) {
      console.error("Avatar upload error:", error);
      toast.error("Échec de l'upload de la photo");
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        phone: profile.phone || "+971",
        address_line1: profile.address_line1 || "",
        address_line2: profile.address_line2 || "",
        city: profile.city || "Dubai",
        emirate: profile.emirate || "Dubai",
        postal_code: profile.postal_code || "",
        marketing_opt_in: profile.marketing_opt_in || false,
      });
    }
  }, [profile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmirateChange = (value: string) => {
    setFormData((prev) => ({ ...prev, emirate: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    const { error } = await updateProfile(formData);
    if (error) {
      toast.error("Échec de la mise à jour du profil");
    } else {
      toast.success("Profil mis à jour !");
      setIsEditing(false);
    }
    setLoading(false);
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        phone: profile.phone || "+971",
        address_line1: profile.address_line1 || "",
        address_line2: profile.address_line2 || "",
        city: profile.city || "Dubai",
        emirate: profile.emirate || "Dubai",
        postal_code: profile.postal_code || "",
        marketing_opt_in: profile.marketing_opt_in || false,
      });
    }
    setIsEditing(false);
  };

  const handleSignOut = async () => {
    await signOut();
    toast.success("Déconnexion réussie");
  };

  const handlePasswordChange = async () => {
    if (!passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères");
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setChangingPassword(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) {
        if (error.message.includes("same")) {
          toast.error("Le nouveau mot de passe doit être différent de l'ancien");
        } else {
          toast.error("Échec du changement de mot de passe");
        }
      } else {
        toast.success("Mot de passe modifié avec succès");
        setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setShowPasswordSection(false);
      }
    } catch (err) {
      toast.error("Une erreur est survenue");
    } finally {
      setChangingPassword(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const initials = `${formData.first_name?.charAt(0) || ""}${formData.last_name?.charAt(0) || ""}`.toUpperCase();

  return (
    <div className="min-h-screen flex flex-col pb-24 bg-background">
      {/* Hero Header with gradient */}
      <header 
        className="relative overflow-hidden px-4 sm:px-6 pt-6 sm:pt-8 pb-6 sm:pb-8"
        style={{ paddingTop: 'max(2.5rem, calc(env(safe-area-inset-top, 0px) + 1.5rem))' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <div className="relative max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            {isAgent && (
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/agent")}
                className="shrink-0 h-9 w-9"
              >
                <ArrowLeft size={18} />
              </Button>
            )}
            <h1 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
              Mon Profil
            </h1>
          </div>

          {/* Avatar Card */}
          <div className="bg-card rounded-2xl border border-border p-5 sm:p-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                {profile?.avatar_url ? (
                  <img 
                    src={profile.avatar_url} 
                    alt="Avatar"
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-primary/30"
                  />
                ) : (
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border-2 border-primary/30">
                    <span className="text-primary font-serif text-xl sm:text-2xl font-semibold">
                      {initials || "?"}
                    </span>
                  </div>
                )}
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
                <button 
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="absolute -bottom-1 -right-1 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center shadow-gold border-2 border-background hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {uploadingAvatar ? (
                    <Loader2 size={12} className="text-primary-foreground animate-spin" />
                  ) : (
                    <Camera size={12} className="text-primary-foreground" />
                  )}
                </button>
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-lg sm:text-xl font-medium text-foreground">
                  {formData.first_name} {formData.last_name}
                </h2>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6 space-y-4 sm:space-y-5 overflow-auto">
        <div className="max-w-2xl mx-auto space-y-4 sm:space-y-5">
          {/* Edit Mode Actions */}
          {isEditing && (
            <div className="flex gap-2 animate-fade-in">
              <Button variant="outline" className="flex-1" onClick={handleCancel} disabled={loading}>
                <X size={16} className="mr-2" />
                Annuler
              </Button>
              <Button variant="gold" className="flex-1" onClick={handleSave} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                Enregistrer
              </Button>
            </div>
          )}

          {/* Profile Form */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-base sm:text-lg font-medium text-foreground">
                Informations personnelles
              </h3>
              {!isEditing && (
                <Button variant="ghost" size="sm" className="text-xs sm:text-sm text-primary" onClick={() => setIsEditing(true)}>
                  Modifier
                </Button>
              )}
            </div>

            <div className="bg-card rounded-xl border border-border p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-xs">Prénom</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={!isEditing ? "bg-muted/50" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-xs">Nom</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={!isEditing ? "bg-muted/50" : ""}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-xs">Téléphone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={!isEditing ? "bg-muted/50" : ""}
                />
              </div>
            </div>
          </section>

          {/* Address Section - Only for regular users */}
          {!isAgent && (
            <section className="space-y-3">
              <h3 className="font-serif text-base sm:text-lg font-medium text-foreground">Adresse</h3>

              <div className="bg-card rounded-xl border border-border p-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="address_line1" className="text-xs">Adresse</Label>
                  <Input
                    id="address_line1"
                    name="address_line1"
                    value={formData.address_line1}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={!isEditing ? "bg-muted/50" : ""}
                    placeholder="Numéro et nom de rue"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address_line2" className="text-xs">Complément</Label>
                  <Input
                    id="address_line2"
                    name="address_line2"
                    value={formData.address_line2}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={!isEditing ? "bg-muted/50" : ""}
                    placeholder="Appartement, bâtiment, etc."
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-xs">Ville</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted/50" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emirate" className="text-xs">Émirat</Label>
                    <Select value={formData.emirate} onValueChange={handleEmirateChange} disabled={!isEditing}>
                      <SelectTrigger className={!isEditing ? "bg-muted/50" : ""}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UAE_EMIRATES.map((emirate) => (
                          <SelectItem key={emirate} value={emirate}>{emirate}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postal_code" className="text-xs">Code postal</Label>
                  <Input
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={!isEditing ? "bg-muted/50" : ""}
                  />
                </div>
              </div>
            </section>
          )}

          {/* Settings Cards */}
          <section className="space-y-3">
            <h3 className="font-serif text-base sm:text-lg font-medium text-foreground">Paramètres</h3>
            
            <div className="space-y-2">
              {/* Notifications Card */}
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Bell size={18} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground text-sm">Notifications</h4>
                    <p className="text-xs text-muted-foreground">Gérer les alertes push</p>
                  </div>
                  <ChevronRight size={18} className="text-muted-foreground shrink-0" />
                </div>
                <div className="px-4 pb-4 pt-0">
                  <NotificationDiagnostics />
                </div>
              </div>

              {/* Marketing Card - Only for regular users */}
              {!isAgent && (
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  <div className="flex items-center gap-3 p-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Mail size={18} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-foreground text-sm">Communications marketing</h4>
                      <p className="text-xs text-muted-foreground">Recevoir nos offres et actualités</p>
                    </div>
                    <Switch
                      checked={formData.marketing_opt_in}
                      onCheckedChange={async (checked) => {
                        setFormData(prev => ({ ...prev, marketing_opt_in: checked }));
                        const { error } = await updateProfile({ marketing_opt_in: checked });
                        if (error) {
                          toast.error("Échec de la mise à jour");
                          setFormData(prev => ({ ...prev, marketing_opt_in: !checked }));
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Password Change Card */}
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <button 
                  onClick={() => setShowPasswordSection(!showPasswordSection)}
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-accent/50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Lock size={18} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground text-sm">Mot de passe</h4>
                    <p className="text-xs text-muted-foreground">Modifier votre mot de passe</p>
                  </div>
                  <ChevronRight size={18} className={`text-muted-foreground shrink-0 transition-transform ${showPasswordSection ? 'rotate-90' : ''}`} />
                </button>
                
                {showPasswordSection && (
                  <div className="px-4 pb-4 pt-0 space-y-3 animate-fade-in">
                    <div className="space-y-2">
                      <Label htmlFor="newPassword" className="text-xs">Nouveau mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="newPassword"
                          type={showPasswords.new ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                          placeholder="Minimum 8 caractères"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-xs">Confirmer le mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showPasswords.confirm ? "text" : "password"}
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          placeholder="Confirmez le nouveau mot de passe"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    
                    <Button
                      variant="gold"
                      size="sm"
                      className="w-full"
                      onClick={handlePasswordChange}
                      disabled={changingPassword}
                    >
                      {changingPassword ? (
                        <>
                          <Loader2 size={14} className="animate-spin mr-2" />
                          Modification...
                        </>
                      ) : (
                        "Modifier le mot de passe"
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Application Card */}
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Smartphone size={18} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground text-sm">Application</h4>
                    <p className="text-xs text-muted-foreground">Installation et mises à jour</p>
                  </div>
                </div>
                <div className="px-4 pb-4 pt-0 space-y-3">
                  <PWAInstallGuide />
                  <AppVersionCard />
                </div>
              </div>
            </div>
          </section>

          {/* Sign Out Section - with proper spacing from bottom nav */}
          <section className="pt-2 pb-4">
            <Button
              variant="outline"
              className="w-full border-destructive/30 text-destructive hover:bg-destructive/10 hover:border-destructive/50"
              onClick={handleSignOut}
            >
              <LogOut size={16} className="mr-2" />
              Se déconnecter
            </Button>
          </section>
        </div>
      </main>

      {isAgent ? <BottomNavAgent /> : <BottomNav />}
    </div>
  );
}